import { format as formatUrl } from 'url';
import NextLink from 'next/link';
import { AnchorHTMLAttributes, useMemo } from 'react';
import { TrackingDataProps } from '../AnalyticsContext/LinkTracking';
import { applyTrackingId } from '../AnalyticsContext/applyTrackingId';
import { renderLinkTrackingAttributes } from '../AnalyticsContext/renderLinkTrackingAttributes';
import { hasTrailingSlash, OpaqueUrl } from '../routing/util';

export type LinkProps = TrackingDataProps & {
  to: OpaqueUrl;
  newTab?: boolean;
} & AnchorHTMLAttributes<object>;

export const Link = ({
  to,
  children,
  trackingData,
  trackingId,
  newTab = false,
  ...rest
}: LinkProps) => {
  const clickTrackingAttributes = renderLinkTrackingAttributes(trackingData);
  const trackingIdProps = applyTrackingId(trackingId);

  const formattedUrl = useMemo(() => formatUrl(to), [to]);

  if (newTab) {
    return (
      <a
        href={formattedUrl}
        {...clickTrackingAttributes}
        {...trackingIdProps}
        {...rest}
        target="_blank"
        rel="noreferrer"
      >
        {children}
      </a>
    );
  }

  /** Nextjs removes trailing slashes before rendering URLs to the DOM, which can break
   * URLs handled by megatron. For such URLs, we avoid wrapping them in a NextLink.
   */
  if (hasTrailingSlash(formattedUrl)) {
    return (
      <a href={formattedUrl} {...clickTrackingAttributes} {...trackingIdProps} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <NextLink href={to}>
      <a {...clickTrackingAttributes} {...trackingIdProps} {...rest}>
        {children}
      </a>
    </NextLink>
  );
};

export const MarkdownLinkWrapper = ({ href, ...props }: AnchorHTMLAttributes<object>) => (
  <Link to={href ?? '#'} newTab {...props} />
);
